/**
 * Frontend-only filter for Jackpots / JW/S Explore views.
 *
 * When the selected insight is Jackpots (`jackpot_value` / `jackpots`) or
 * JW/S (`jackpot_value_per_spin`), drop EGMs whose jackpot amount or jackpot
 * count is not strictly positive so they never appear in the list, map pins /
 * heat, Top 10 selection, or client-side counts.
 *
 * Fields:
 * - amount → `jackpot_value`
 * - count  → `jackpots`
 *
 * null / undefined / NaN coerce to 0 (and are therefore filtered out).
 * Other insights pass rows through unchanged.
 */

export const ZERO_JACKPOT_FILTER_INSIGHTS = [
  'jackpot_value',
  'jackpots',
  'jackpot_value_per_spin',
] as const;

export type ZeroJackpotFilterInsight = (typeof ZERO_JACKPOT_FILTER_INSIGHTS)[number];

export type JackpotAmountCountRow = {
  jackpot_value?: unknown;
  jackpots?: unknown;
};

/** Coerce a metric to a finite number; null/undefined/NaN → 0. */
export function coerceJackpotMetric(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function shouldFilterZeroJackpotEgms(
  insight: string | null | undefined
): insight is ZeroJackpotFilterInsight {
  return (
    insight === 'jackpot_value' ||
    insight === 'jackpots' ||
    insight === 'jackpot_value_per_spin'
  );
}

/** True when both amount and count are strictly > 0. */
export function hasPositiveJackpotAmountAndCount(row: JackpotAmountCountRow): boolean {
  const amount = coerceJackpotMetric(row.jackpot_value);
  const count = coerceJackpotMetric(row.jackpots);
  return amount > 0 && count > 0;
}

/**
 * For Jackpots / JW/S insights, keep only EGMs with amount > 0 AND count > 0.
 * Returns the same array reference when the insight does not apply.
 */
export function rejectZeroJackpotEgms<T extends JackpotAmountCountRow>(
  rows: T[],
  insight: string | null | undefined
): T[] {
  if (!shouldFilterZeroJackpotEgms(insight)) return rows;
  return rows.filter(hasPositiveJackpotAmountAndCount);
}
